import { useEffect, useMemo } from "react";
import { Link, useLocation, useOutletContext } from "react-router-dom";

import type { ShellOutletContext } from "../nav/AppShell";
import { AdminSectionHead } from "../components/AdminSectionHead";
import { ShellSubrail } from "../components/shell/ShellSubrail";
import { WorkbenchLayout } from "../components/shell/WorkbenchLayout";
import { MATRIX_MANAGE_PERMISSION } from "../data/role-permission-matrix";
import { fetchAccountConsoleModules } from "../data/console-modules";
import { useReadModel } from "../data/useReadModel";
import { scrollToPageSection } from "../lib/scroll-to-section";
import { useSectionScrollSpy } from "../lib/useSectionScrollSpy";
import { buildAdminSections } from "../surfaces/admin-sections";
import { AlertChannelPolicyPanel } from "../surfaces/AlertChannelPolicyPanel";
import { AuditFeed } from "../surfaces/AuditFeed";
import { BrandProfile } from "../surfaces/BrandProfile";
import { CommercialAdminReadiness } from "../surfaces/CommercialAdminReadiness";
import { ConsentPolicyPanel } from "../surfaces/ConsentPolicyPanel";
import { DeviceInventory } from "../surfaces/DeviceInventory";
import { DutyTrackingPolicyRow } from "../surfaces/DutyTrackingPolicyRow";
import { MemberAlertChannelPreferencesPanel } from "../surfaces/MemberAlertChannelPreferencesPanel";
import { MemberDirectory } from "../surfaces/MemberDirectory";
import { MemberInvitePanel } from "../surfaces/MemberInvitePanel";
import { MobileFeatureFlagsPanel } from "../surfaces/MobileFeatureFlagsPanel";
import { PermissionsTabs } from "../surfaces/PermissionsTabs";
import { PolicyReadPanel } from "../surfaces/PolicyReadPanel";
import { ReleaseStatus } from "../surfaces/ReleaseStatus";

// Admin workbench (console shell v3, Wave 1): management surfaces over the hosted console
// read models (Members, Devices, Audit, Branding, Release health), reached via the
// member.manage-gated nav item. Unlike the switching workbenches, Admin keeps its sections
// stacked - policy panels read best in sequence - and the ShellSubrail is the jump-group
// rail: selecting a group scrolls the stage to it, and the scroll spy keeps the rail honest
// on the way back up. Members and Devices carry the Wave B lifecycle mutations
// (suspend/restore/remove, device revoke/restore) through hosted RPCs only; the remaining
// surfaces stay read-only.
export function AdminPage() {
  const { config } = useOutletContext<ShellOutletContext>();
  return <AccountScopedAdminPage key={config.accountId} />;
}

/** Remount on workspace switches so module state and every nested read fail closed together. */
function AccountScopedAdminPage() {
  const { client, config, can, isSuperadmin, refreshPermissions } = useOutletContext<ShellOutletContext>();
  const location = useLocation();
  const canViewPolicy = can("policy.view");
  /** One value, read twice: it gates the Permissions section and the subrail entry that jumps to it. */
  const canManagePermissions = can(MATRIX_MANAGE_PERMISSION);
  const surfaceProps = {
    client,
    accountId: config.accountId,
    timeZone: config.displayTimeZone,
    pollMs: config.refreshMs,
  };
  const consoleModules = useReadModel(
    () => fetchAccountConsoleModules(client, config.accountId),
    [client, config.accountId],
    config.refreshMs,
  );
  const adminBrandingEnabled = consoleModules.status === "ready"
    && consoleModules.data?.adminBranding === true;
  const brandingDeepLinkRequested = location.hash === "#admin-branding";
  const sections = useMemo(
    () => buildAdminSections(canViewPolicy, canManagePermissions, adminBrandingEnabled),
    [canViewPolicy, canManagePermissions, adminBrandingEnabled],
  );
  const sectionIds = useMemo(() => sections.map((section) => section.id), [sections]);
  const { activeId, setActiveId } = useSectionScrollSpy(sectionIds);

  useEffect(() => {
    if (!brandingDeepLinkRequested || adminBrandingEnabled || consoleModules.status === "loading") {
      return;
    }
    const target = document.getElementById("admin-branding");
    if (typeof target?.scrollIntoView === "function") {
      target.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [adminBrandingEnabled, brandingDeepLinkRequested, consoleModules.status]);

  function jumpToSection(sectionId: string): void {
    setActiveId(sectionId);
    scrollToPageSection(sectionId, "admin__section--pulse");
  }

  return (
    <WorkbenchLayout
      className="admin admin-page"
      subrail={
        <ShellSubrail
          eyebrow="Admin"
          items={sections.map(({ id, label }) => ({ key: id, label }))}
          {...(activeId !== undefined ? { activeKey: activeId } : {})}
          onSelect={jumpToSection}
        />
      }
      head={
        <div className="wb-head">
          <div className="wb-head__row">
            <h1 className="wb-head__title">Admin</h1>
            <p className="wb-head__note">
              Account management over the hosted console read models. Member lifecycle
              (suspend/restore/remove) and device trust (revoke/restore) run through audited
              RPCs; removed members revive when re-invited. Brand editing still lands in a
              later slice.
            </p>
          </div>
        </div>
      }
    >
      {canViewPolicy ? (
        <section id="admin-policies" className="admin__section" aria-label="Site policies">
          <AdminSectionHead
            title="Site policies"
            scope="site"
            hint="Capture, alert, scheduling, and timeclock values per site. Use the site picker inside this section. Branding and privacy are account-wide."
          />
          <PolicyReadPanel
            client={client}
            accountId={config.accountId}
            canView
            canManage={can("policy.manage")}
            hiddenDomains={adminBrandingEnabled ? [] : ["branding"]}
          />
        </section>
      ) : null}

      {canViewPolicy ? (
        <section id="admin-duty" className="admin__section" aria-label="Duty tracking">
          <AdminSectionHead
            title="Duty tracking"
            scope="account"
            hint="On-duty location accountability applies to the whole account. Guards load this policy on duty start; CAD roster staleness uses the same settings."
          />
          <DutyTrackingPolicyRow
            client={client}
            accountId={config.accountId}
            canView
            canManage={can("policy.manage")}
            pollMs={config.refreshMs}
          />
        </section>
      ) : null}

      {canViewPolicy ? (
        <section id="admin-mobile-flags" className="admin__section" aria-label="Mobile feature flags">
          <AdminSectionHead
            title="Mobile feature flags"
            scope="account"
            hint="Default runtime toggles every guard app resolves on launch for this account."
          />
          <MobileFeatureFlagsPanel
            client={client}
            accountId={config.accountId}
            canView
            canManage={can("policy.manage")}
            pollMs={config.refreshMs}
          />
        </section>
      ) : null}

      {canViewPolicy ? (
        <section id="admin-alert-channels" className="admin__section" aria-label="Alerts and channel policies">
          <AdminSectionHead
            title="Alerts & channel policies"
            scope="account"
            hint="Which channels each event fires on, for the whole account. This is the floor members cannot drop below; quiet hours are optional and never hold safety alerts."
          />
          <AlertChannelPolicyPanel
            client={client}
            accountId={config.accountId}
            canView
            canManage={can("policy.manage")}
            defaultTimeZone={config.displayTimeZone}
            pollMs={config.refreshMs}
          />
        </section>
      ) : null}

      {/* Deliberately NOT behind canViewPolicy: these are the signed-in member's own additive
          preferences, owned by them and bound to auth.uid() server-side. The account floor beside
          them is passed only when this caller already holds policy.view. */}
      <section id="admin-my-alert-channels" className="admin__section" aria-label="My alert channels">
        <AdminSectionHead
          title="My alert channels"
          scope="account"
          hint="Extra ways you personally want to be notified, on top of the account rules. Additions only: switching something off here never stops a channel the account sends everyone, and SOS and duress always reach you."
        />
        <MemberAlertChannelPreferencesPanel
          client={client}
          accountId={config.accountId}
          canViewAccountFloor={canViewPolicy}
        />
      </section>

      {canViewPolicy ? (
        <section id="admin-consent" className="admin__section" aria-label="Consent policies">
          <AdminSectionHead
            title="Consent policies"
            scope="account"
            hint="Field-duty consent versions for on-duty safety across the account."
          />
          <ConsentPolicyPanel
            client={client}
            accountId={config.accountId}
            canView
            canManage={can("policy.manage")}
            pollMs={config.refreshMs}
          />
        </section>
      ) : null}

      <section id="admin-members" className="admin__section admin-members" aria-label="Members">
        <AdminSectionHead
          title="Members"
          scope="account"
          hint={
            can("member.manage")
              ? "Invite supervisors and guards, edit names and phones, promote or demote roles, and suspend, restore, or remove members. Suspend and remove release future assignments for reassignment; removed members revive when re-invited."
              : "The account roster (read-only)."
          }
        />
        <MemberInvitePanel
          client={client}
          accountId={config.accountId}
          timeZone={config.displayTimeZone}
          canManage={can("member.manage")}
        />
        {/* canSupportAs rides the permission call's own superadmin `source`, so it is already
            false while a support session is open — the affordance cannot be used to stack a
            second act-as on top of the first. The server refuses regardless. */}
        <MemberDirectory {...surfaceProps} canManage={can("member.manage")} canSupportAs={isSuperadmin} />
      </section>

      {/* PRD-001AE AE3 through AE5. Sits directly under Members because the two answer adjacent
          questions: Members is who holds which role, this is what a role is allowed to do.
          Deliberately NOT grouped with the policy panels above, which are runtime settings and are
          never called permissions (AC-001AE.5). The Audit tab is a filtered read of the same
          account audit trail the Audit section below renders in full. */}
      {canManagePermissions ? (
        <section id="admin-permissions" className="admin__section" aria-label="Permissions">
          <AdminSectionHead
            title="Permissions"
            scope="account"
            hint="What each role may do in this workspace, who holds which role, and what was changed. These are capability keys, not the duty, mobile, and alert settings above."
          />
          <PermissionsTabs
            client={client}
            accountId={config.accountId}
            timeZone={config.displayTimeZone}
            canView
            canManage={canManagePermissions}
            onPermissionsChanged={refreshPermissions}
          />
        </section>
      ) : null}

      <section id="admin-devices" className="admin__section" aria-label="Devices">
        <AdminSectionHead
          title="Devices"
          scope="account"
          hint={
            can("schedule.manage")
              ? "Registered handsets and trust posture. Revoke blocks a device at its next registration or scan sync (reason required); restore re-trusts it."
              : "Registered handsets and trust posture for this account."
          }
        />
        {/* Display gate can("schedule.manage") matches the hosted RPC's admin+supervisor
            role audience exactly; the server enforces the roles regardless. */}
        <DeviceInventory {...surfaceProps} canManage={can("schedule.manage")} />
      </section>

      <section id="admin-audit" className="admin__section" aria-label="Audit">
        <AdminSectionHead title="Audit" scope="account" hint="Recent management activity for this account." />
        <AuditFeed {...surfaceProps} />
      </section>

      {adminBrandingEnabled ? (
        <section id="admin-branding" className="admin__section" aria-label="Branding">
          <AdminSectionHead title="Branding" scope="account" />
          <BrandProfile {...surfaceProps} />
        </section>
      ) : brandingDeepLinkRequested ? (
        <section id="admin-branding" className="admin__section" aria-label="Branding unavailable">
          <AdminSectionHead title="Branding" scope="account" />
          {consoleModules.status === "loading" ? (
            <p className="surface__state" role="status">Confirming Branding availability...</p>
          ) : consoleModules.status === "error" ? (
            <div className="admin-module-unavailable" role="alert">
              <strong>Branding availability could not be confirmed</strong>
              <p>Branding stays hidden until this workspace's Console modules can be loaded.</p>
              <Link to="/admin">Back to Admin</Link>
            </div>
          ) : (
            <div className="admin-module-unavailable" role="status">
              <strong>Branding is not enabled for this workspace</strong>
              <p>
                Existing brand data is retained. A platform superadmin can enable Branding from
                Platform Accounts, Capabilities.
              </p>
              <Link to="/admin">Back to Admin</Link>
            </div>
          )}
        </section>
      ) : null}

      <section id="admin-commercial" className="admin__section" aria-label="Commercial readiness">
        <AdminSectionHead title="Commercial readiness" scope="account" />
        <CommercialAdminReadiness
          client={client}
          accountId={config.accountId}
          pollMs={config.refreshMs}
          canManageBilling={can("billing.manage")}
          canManageBrand={can("brand.manage")}
        />
      </section>

      <section id="admin-release" className="admin__section" aria-label="Release health">
        <AdminSectionHead title="Release health" scope="account" />
        <ReleaseStatus {...surfaceProps} />
      </section>
    </WorkbenchLayout>
  );
}
